import Phaser from "phaser";
import type { BrickCellKind } from "../data/ChapterData.js";
import type { WorldBrickTextures, WorldVisualFamily } from "../data/worldData.js";
import type { BrickTextureSpec } from "./brickAppearance.js";

export interface BrickVisualTextureSpec {
  readonly base: string;
  readonly overlay: string;
}

export interface BrickVisualDisplaySize {
  readonly width: number;
  readonly height: number;
}

export const CIRCUIT_BRICK_VISUAL_SCALE = 1.16;
export const CIRCUIT_MYSTERY_VISUAL_SCALE = 0.86;
export const CIRCUIT_GOLD_VISUAL_SCALE = 0.95;
export const CIRCUIT_COMMON_GOLD_VISUAL_SCALE = 0.72;
export const CIRCUIT_LABEL_MIN_SCALE = 0.5;
export const CIRCUIT_LABEL_BACKGROUND = "rgba(0,0,0,0)";

const BRICK_SQUARE_ASPECT_TOLERANCE = 1.25;
const BRICK_WIDE_ASPECT_THRESHOLD = 3;
const BRICK_TEXTURE_PATTERN_VARIANTS = 2;
const COMPOSITE_BRICK_TEXTURE_PREFIX = "__brickComposite";
const SPACE_BRICK_BASE_KEY = "world_space_brick";
const SPACE_BRICK_VISUAL_MAX_HEIGHT_RATIO = 1.45;
const SPACE_BRICK_VISUAL_ASPECTS: Record<string, number> = {
  [SPACE_BRICK_BASE_KEY]: 1.16,
  [`${SPACE_BRICK_BASE_KEY}_v1`]: 1.34,
  [`${SPACE_BRICK_BASE_KEY}_v2`]: 1.56,
  [`${SPACE_BRICK_BASE_KEY}_v3`]: 1.24,
};

function textureVariantKey(key: string, variant: "square" | "wide"): string {
  return `${key}_${variant}`;
}

function texturePatternVariantKey(key: string, patternIndex: number): string {
  return `${key}_v${patternIndex}`;
}

function isSquareLikeBrick(width: number, height: number): boolean {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  return Math.max(w, h) / Math.min(w, h) <= BRICK_SQUARE_ASPECT_TOLERANCE;
}

function isWideBrick(width: number, height: number): boolean {
  return Math.max(1, width) / Math.max(1, height) >= BRICK_WIDE_ASPECT_THRESHOLD;
}

function texturePatternIndex(gridX: number, gridY: number, salt: number): number {
  const mixed =
    Math.imul(gridX + 17, 374_761_393) ^
    Math.imul(gridY + 31, 668_265_263) ^
    Math.imul(salt + 101, 2_246_822_519);
  return 1 + ((mixed >>> 0) % BRICK_TEXTURE_PATTERN_VARIANTS);
}

function textureStrengthPatternIndex(strength01: number): number {
  const s = Phaser.Math.Clamp(strength01, 0, 1);
  if (s < 0.25) return 0;
  if (s < 0.5) return 1;
  if (s < 0.82) return 2;
  return 3;
}

function textureOptionalPatternVariantKey(key: string, patternIndex: number): string {
  return patternIndex <= 0 ? key : texturePatternVariantKey(key, patternIndex);
}

function visualFamilyFor(worldTextures?: WorldBrickTextures): WorldVisualFamily {
  return worldTextures?.visualFamily ?? "default";
}

function spaceBrickVisualAspect(key: string): number {
  return SPACE_BRICK_VISUAL_ASPECTS[key] ?? SPACE_BRICK_VISUAL_ASPECTS[SPACE_BRICK_BASE_KEY];
}

function shouldUseSpaceStrengthVariant(
  tex: BrickTextureSpec,
  worldTextures: WorldBrickTextures | undefined,
): boolean {
  return (
    visualFamilyFor(worldTextures) === "space" &&
    tex.base === worldTextures?.normalBase &&
    tex.overlay === worldTextures.overlay
  );
}

function safeTextureKeyPart(key: string): string {
  return key.replace(/[^a-zA-Z0-9_]+/g, "_");
}

function compositeBrickTextureKey(baseKey: string, overlayKey: string): string {
  return `${COMPOSITE_BRICK_TEXTURE_PREFIX}_${safeTextureKeyPart(baseKey)}__${safeTextureKeyPart(overlayKey)}`;
}

export function isCompositeBrickTextureKey(key: string): boolean {
  return key.startsWith(`${COMPOSITE_BRICK_TEXTURE_PREFIX}_`);
}

function drawFrameToCanvas(
  ctx: CanvasRenderingContext2D,
  frame: Phaser.Textures.Frame,
  width: number,
  height: number,
): boolean {
  const image = frame.source.image as CanvasImageSource | undefined;
  if (!image) return false;
  ctx.drawImage(image, frame.cutX, frame.cutY, frame.cutWidth, frame.cutHeight, 0, 0, width, height);
  return true;
}

export function visualScaleForBrick(
  tex: BrickTextureSpec,
  kind: BrickCellKind,
  worldTextures?: WorldBrickTextures,
): number {
  const family = visualFamilyFor(worldTextures);
  if (kind === "gold") {
    if (family !== "circuit") return 1;
    return tex.base === worldTextures?.goldBase ? CIRCUIT_GOLD_VISUAL_SCALE : CIRCUIT_COMMON_GOLD_VISUAL_SCALE;
  }
  if (family !== "circuit") return 1;
  return kind === "mystery" ? CIRCUIT_MYSTERY_VISUAL_SCALE : CIRCUIT_BRICK_VISUAL_SCALE;
}

export function resolveVisualDisplaySize(
  visualTex: BrickVisualTextureSpec,
  width: number,
  height: number,
  worldTextures?: WorldBrickTextures,
): BrickVisualDisplaySize {
  const cellWidth = Math.max(1, width);
  const cellHeight = Math.max(1, height);
  if (visualFamilyFor(worldTextures) !== "space") return { width: cellWidth, height: cellHeight };

  const aspect = spaceBrickVisualAspect(visualTex.base);
  const maxHeight = Math.max(cellHeight, Math.min(cellWidth, cellHeight * SPACE_BRICK_VISUAL_MAX_HEIGHT_RATIO));
  let displayHeight = Math.min(maxHeight, cellWidth / aspect);
  let displayWidth = displayHeight * aspect;
  if (displayWidth > cellWidth) {
    displayWidth = cellWidth;
    displayHeight = displayWidth / aspect;
  }
  return {
    width: Math.max(1, displayWidth),
    height: Math.max(1, displayHeight),
  };
}

export function resolveVisualTextures(
  textures: Phaser.Textures.TextureManager,
  tex: BrickTextureSpec,
  width: number,
  height: number,
  gridX: number,
  gridY: number,
  salt: number,
  strength01 = Number.NaN,
  worldTextures?: WorldBrickTextures,
): BrickVisualTextureSpec {
  const variant = isSquareLikeBrick(width, height) ? "square" : isWideBrick(width, height) ? "wide" : null;
  const variantBase = variant ? textureVariantKey(tex.base, variant) : tex.base;
  const variantOverlay = variant ? textureVariantKey(tex.overlay, variant) : tex.overlay;
  const base = textures.exists(variantBase) ? variantBase : tex.base;
  const overlay = textures.exists(variantOverlay) ? variantOverlay : tex.overlay;
  if (shouldUseSpaceStrengthVariant(tex, worldTextures) && Number.isFinite(strength01)) {
    const tierPattern = textureStrengthPatternIndex(strength01);
    const tierBase = textureOptionalPatternVariantKey(base, tierPattern);
    const tierOverlay = textureOptionalPatternVariantKey(overlay, tierPattern);
    return {
      base: textures.exists(tierBase) ? tierBase : base,
      overlay: textures.exists(tierOverlay) ? tierOverlay : overlay,
    };
  }
  const pattern = texturePatternIndex(gridX, gridY, salt);
  const patternBase = texturePatternVariantKey(base, pattern);
  const patternOverlay = texturePatternVariantKey(overlay, pattern);
  return {
    base: textures.exists(patternBase) ? patternBase : base,
    overlay: textures.exists(patternOverlay) ? patternOverlay : overlay,
  };
}

export function shouldUseCompositeVisualTexture(
  tex: BrickTextureSpec,
  visualTex: BrickVisualTextureSpec,
  worldTextures?: WorldBrickTextures,
): boolean {
  return visualFamilyFor(worldTextures) === "circuit" && !tex.tintBase && !tex.tintOverlay && visualTex.base !== visualTex.overlay;
}

export function ensureCompositeBrickTexture(
  scene: Phaser.Scene,
  baseKey: string,
  overlayKey: string,
): string | undefined {
  const key = compositeBrickTextureKey(baseKey, overlayKey);
  if (scene.textures.exists(key)) return key;
  const baseFrame = scene.textures.getFrame(baseKey);
  const overlayFrame = scene.textures.getFrame(overlayKey);
  if (!baseFrame || !overlayFrame) return undefined;
  const width = Math.max(1, Math.floor(baseFrame.cutWidth));
  const height = Math.max(1, Math.floor(baseFrame.cutHeight));
  const texture = scene.textures.createCanvas(key, width, height);
  if (!texture) return undefined;
  const ctx = texture.getContext();
  ctx.clearRect(0, 0, width, height);
  const ok = drawFrameToCanvas(ctx, baseFrame, width, height) && drawFrameToCanvas(ctx, overlayFrame, width, height);
  if (!ok) {
    scene.textures.remove(key);
    return undefined;
  }
  texture.refresh();
  return key;
}
